import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { Button } from "@shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Activity, BarChart3, TrendingDown, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LowStockDashboard } from "@features/products/components/LowStockDashboard";
import { RecentStockMovements } from "@features/products/components/RecentStockMovements";
import HeadLayout from "@shared/layouts/HeadLayout";

export default function StockAnalytics() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("recent-movements");

  return (
    <>
      <HeadLayout title={"products.stockAnalytics"} />
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              {t("products.stockAnalytics")}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t("products.stockAnalyticsDescription")}
            </p>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {t("products.recentMovements")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">124</div>
              <p className="text-xs text-muted-foreground">
                {t("products.last7Days")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                {t("products.lowStockItems")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">8</div>
              <p className="text-xs text-muted-foreground">
                {t("products.needsAttention")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Package className="w-4 h-4" />
                {t("products.outOfStock")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">3</div>
              <p className="text-xs text-muted-foreground">
                {t("products.requiresImmediate")}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("products.totalValue")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">$24,567</div>
              <p className="text-xs text-muted-foreground">
                {t("products.inventoryValue")}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger
              value="recent-movements"
              className="flex items-center gap-2"
            >
              <Activity className="w-4 h-4" />
              {t("products.recentMovements")}
            </TabsTrigger>
            <TabsTrigger value="low-stock" className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4" />
              {t("products.lowStockMonitoring")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="recent-movements" className="space-y-4">
            <RecentStockMovements />
          </TabsContent>

          <TabsContent value="low-stock" className="space-y-4">
            <LowStockDashboard />
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>{t("products.quickActions")}</CardTitle>
            <CardDescription>
              {t("products.quickActionsDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Button variant="outline">
                <Package className="w-4 h-4 mr-2" />
                {t("products.bulkStockUpdate")}
              </Button>
              <Button variant="outline">
                <TrendingDown className="w-4 h-4 mr-2" />
                {t("products.generateLowStockReport")}
              </Button>
              <Button variant="outline">
                <Activity className="w-4 h-4 mr-2" />
                {t("products.exportMovementHistory")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
